import {
  sanitizePurchaseReturnPath,
  SsoConfigurationError,
  validateLazIoHandoffUrl,
} from "./ssoUtils";
import {
  getSsoPartner,
  SSO_PARTNERS,
  SSO_PROTOCOLS,
} from "./ssoConfig";

export const SSO_CONTEXT_KEY = "laz_sso_ctx";
export const SSO_CONTEXT_VERSION = 1;
export const SSO_CONTEXT_TTL_MS = 10 * 60 * 1000;

export class SsoContextError extends Error {
  constructor(message, code = "INVALID_SSO_CONTEXT") {
    super(message);
    this.name = "SsoContextError";
    this.code = code;
  }
}

function getStorage(storage) {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
}

function normalizeProvider(provider) {
  const id = provider?.id;
  const authProtocol = String(provider?.authProtocol || "").toUpperCase();
  if (
    id == null ||
    ![SSO_PROTOCOLS.OIDC, SSO_PROTOCOLS.CAS].includes(authProtocol)
  ) {
    throw new SsoContextError(
      "The saved sign-in provider is invalid.",
      "INVALID_PROVIDER"
    );
  }

  return {
    id,
    name: String(provider?.name || ""),
    authProtocol,
  };
}

export function createSsoContext(
  {
    locationId,
    productId = 0,
    widgetKey = "",
    externalGroupId = "",
    returnUrl,
    provider,
    partner = null,
    v2Callback = null,
    v2LocationId = null,
  },
  options = {}
) {
  const origin = options.origin || window.location.origin;
  const hostname = options.hostname || new URL(origin).hostname;
  const normalizedLocationId = String(locationId || "");
  if (!normalizedLocationId) {
    throw new SsoContextError(
      "The sign-in location is missing.",
      "MISSING_LOCATION"
    );
  }

  let validatedReturnPath;
  let validatedHandoff;
  try {
    validatedReturnPath = sanitizePurchaseReturnPath(returnUrl, origin);
    const returnUrlWithLocation = new URL(validatedReturnPath, origin);
    returnUrlWithLocation.searchParams.set("l", normalizedLocationId);
    validatedReturnPath =
      returnUrlWithLocation.pathname + returnUrlWithLocation.search;
    validatedHandoff = validateLazIoHandoffUrl(v2Callback, hostname);
  } catch (error) {
    if (error instanceof SsoConfigurationError) {
      throw new SsoContextError(error.message, error.code);
    }
    throw error;
  }

  return {
    version: SSO_CONTEXT_VERSION,
    createdAt: options.now ?? Date.now(),
    locationId: normalizedLocationId,
    productId: Number.isFinite(Number(productId)) ? Number(productId) : 0,
    widgetKey: String(widgetKey || ""),
    externalGroupId: String(externalGroupId || ""),
    returnPath: validatedReturnPath,
    provider: normalizeProvider(provider),
    partner,
    v2Callback: validatedHandoff,
    v2LocationId: v2LocationId ? String(v2LocationId) : null,
  };
}

export function validateSsoContext(context, options = {}) {
  if (!context || typeof context !== "object") {
    throw new SsoContextError(
      "The saved sign-in session is missing.",
      "MISSING_CONTEXT"
    );
  }
  if (context.version !== SSO_CONTEXT_VERSION) {
    throw new SsoContextError(
      "The saved sign-in session is no longer supported.",
      "UNSUPPORTED_CONTEXT_VERSION"
    );
  }

  const now = options.now ?? Date.now();
  if (
    !Number.isFinite(context.createdAt) ||
    context.createdAt > now + 1000 ||
    now - context.createdAt > SSO_CONTEXT_TTL_MS
  ) {
    throw new SsoContextError(
      "The saved sign-in session has expired.",
      "EXPIRED_CONTEXT"
    );
  }

  const normalized = createSsoContext(
    {
      ...context,
      returnUrl: context.returnPath,
      partner: context.partner ?? null,
    },
    {
      ...options,
      now: context.createdAt,
    }
  );

  if (
    normalized.provider.authProtocol === SSO_PROTOCOLS.OIDC &&
    getSsoPartner(normalized.locationId) !== SSO_PARTNERS.NU
  ) {
    throw new SsoContextError(
      "The saved OIDC sign-in session does not match this location.",
      "PROVIDER_LOCATION_MISMATCH"
    );
  }

  return normalized;
}

export function saveSsoContext(context, storage) {
  const target = getStorage(storage);
  if (!target) {
    throw new SsoContextError(
      "Session storage is unavailable.",
      "STORAGE_UNAVAILABLE"
    );
  }

  const validated = validateSsoContext(context);
  try {
    target.setItem(SSO_CONTEXT_KEY, JSON.stringify(validated));
  } catch (error) {
    throw new SsoContextError(
      "Unable to save the sign-in session.",
      "STORAGE_WRITE_FAILED"
    );
  }
  return validated;
}

export function readSsoContext(storage, options = {}) {
  const target = getStorage(storage);
  if (!target) {
    throw new SsoContextError(
      "Session storage is unavailable.",
      "STORAGE_UNAVAILABLE"
    );
  }

  let serialized;
  try {
    serialized = target.getItem(SSO_CONTEXT_KEY);
  } catch (error) {
    throw new SsoContextError(
      "Unable to read the sign-in session.",
      "STORAGE_READ_FAILED"
    );
  }
  if (!serialized) {
    throw new SsoContextError(
      "The saved sign-in session is missing.",
      "MISSING_CONTEXT"
    );
  }

  let parsed;
  try {
    parsed = JSON.parse(serialized);
  } catch (error) {
    clearSsoContext(target);
    throw new SsoContextError(
      "The saved sign-in session is invalid.",
      "MALFORMED_CONTEXT"
    );
  }

  try {
    return validateSsoContext(parsed, options);
  } catch (error) {
    clearSsoContext(target);
    throw error;
  }
}

export function consumeSsoContext(storage, options = {}) {
  const target = getStorage(storage);
  const context = readSsoContext(target, options);
  clearSsoContext(target);
  return context;
}

export function clearSsoContext(storage) {
  const target = getStorage(storage);
  if (!target) return;
  try {
    target.removeItem(SSO_CONTEXT_KEY);
  } catch (error) {
    // A storage failure must not prevent local sign-out or callback cleanup.
  }
}
